"use client";

import { useCallback, useEffect, useState } from "react";
import { useLabsApiAuth } from "@/lib/labsIdentityContext";
import { updateMyLabsSettings } from "@/lib/labsApi";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

/**
 * Signed-in user settings dialog. Rendered only while open (mount fresh each time).
 * - Active accounts: opt out of emails, or disable the account.
 * - Disabled accounts: reactivate the account.
 * The account is never deleted; a disabled user can sign back in and reactivate.
 */
export default function UserSettingsDialog({ onClose }: { onClose: () => void }) {
  const { profile, headers, refresh } = useLabsApiAuth();
  const isDisabled = profile?.isDisabled ?? false;

  const [emailOptOut, setEmailOptOut] = useState(profile?.emailOptOut ?? false);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [saving, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [handleClose]);

  async function apply(
    changes: { emailOptOut?: boolean; isDisabled?: boolean },
    { close }: { close: boolean }
  ) {
    if (!API_URL) {
      setError("API URL not configured.");
      return;
    }
    setSaving(true);
    setError(null);
    const updated = await updateMyLabsSettings(API_URL, headers, changes);
    setSaving(false);
    if (!updated) {
      setError("Could not save your settings. Please try again.");
      return;
    }
    refresh();
    if (close) onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="user-settings-title"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-[var(--stratus-border)] bg-[var(--background)] p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <h2
            id="user-settings-title"
            className="text-xl font-semibold text-[var(--foreground)]"
          >
            {isDisabled ? "Account disabled" : "User settings"}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            aria-label="Close"
            className="rounded-md p-1 text-[var(--stratus-muted)] hover:bg-[var(--stratus-border)]/30 hover:text-[var(--foreground)] disabled:opacity-50"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
            </svg>
          </button>
        </div>

        {isDisabled ? (
          <div className="mt-4">
            <p className="text-sm text-[var(--stratus-muted)]">
              Your account is disabled. Your data has been kept — reactivate your
              account to regain access to experiments and the rest of the site.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={saving}
                className="rounded-md border border-[var(--stratus-border)] bg-[var(--stratus-card)] px-4 py-2 text-sm font-medium text-[var(--foreground)] hover:bg-[var(--stratus-border)]/20 disabled:opacity-50"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={() => apply({ isDisabled: false }, { close: true })}
                disabled={saving}
                className="rounded-md bg-[var(--stratus-primary)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                {saving ? "Reactivating…" : "Reactivate my account"}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-4 space-y-6">
            {/* Email preferences */}
            <section>
              <h3 className="text-sm font-semibold text-[var(--foreground)]">
                Email preferences
              </h3>
              <label className="mt-2 flex items-start gap-3 text-sm text-[var(--foreground)]">
                <input
                  type="checkbox"
                  checked={emailOptOut}
                  disabled={saving}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setEmailOptOut(next);
                    void apply({ emailOptOut: next }, { close: false });
                  }}
                  className="mt-0.5 h-4 w-4 rounded border-[var(--stratus-border)]"
                />
                <span>
                  Opt out of receiving emails
                  <span className="mt-0.5 block text-xs text-[var(--stratus-muted)]">
                    You will no longer receive experiment announcements or other
                    Stratus Labs emails.
                  </span>
                </span>
              </label>
            </section>

            {/* Account */}
            <section className="border-t border-[var(--stratus-border)] pt-5">
              <h3 className="text-sm font-semibold text-[var(--foreground)]">
                Account
              </h3>
              {!confirmDisable ? (
                <div className="mt-2">
                  <p className="text-xs text-[var(--stratus-muted)]">
                    Disabling your account hides the rest of the site. Your data is
                    kept, and you can reactivate any time by signing in and selecting
                    your name.
                  </p>
                  <button
                    type="button"
                    onClick={() => setConfirmDisable(true)}
                    disabled={saving}
                    className="mt-3 rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/30"
                  >
                    Disable my account
                  </button>
                </div>
              ) : (
                <div className="mt-2 rounded-md border border-red-300 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
                  <p className="text-sm text-red-800 dark:text-red-200">
                    Disable your account now? You will be limited to the homepage
                    until you reactivate.
                  </p>
                  <div className="mt-3 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setConfirmDisable(false)}
                      disabled={saving}
                      className="rounded-md border border-[var(--stratus-border)] bg-[var(--stratus-card)] px-3 py-1.5 text-sm font-medium text-[var(--foreground)] hover:bg-[var(--stratus-border)]/20 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => apply({ isDisabled: true }, { close: true })}
                      disabled={saving}
                      className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      {saving ? "Disabling…" : "Disable account"}
                    </button>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}

        {error && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
      </div>
    </div>
  );
}
